import { concatMap, type Observable } from 'rxjs'
import { RealtimeEvent } from '@taxfyle/api-internal/internal/realtime_pb'
import { filter } from 'rxjs/operators'
import { action, observable } from 'mobx'
import { Id } from '@/infra/id'
import type { FetchQuestionsFn, ListSearchableJobsFn } from '@/features/jobs'
import {
  JobWidget,
  type JobWidgetProps,
} from '@/features/jobs/job-widget/JobWidget'
import { withAbortSignal } from '@/utils/rx-util'
import {
  mapJobProtoStatusToStatus,
  mapJobProtoToTitle,
} from '@/features/jobs/job-mappers'
import {
  maybeParseIntervalToTimeRange,
  safeDecimalToNumber,
} from '@/utils/grpc-util'
import {
  mapStepProtoToProps,
  mapStepStatusProtoToStepStatus,
} from '@/features/jobs/taxes/progression/progression-mappers'
import type { JobStatus } from '@/features/jobs/job-status'
import { mapJobScopeChangeStatusProtoToStatus } from '@/features/jobs/job-scope-changes/job-scope-changes-mappers'
import { mapConsultationStatusProtoToStatus } from '@/features/jobs/consultations/consultation-mappers'
import type { GetCurrentWorkspaceFn } from '@/features/iam'

export class JobWidgetStore {
  @observable accessor jobWidget: JobWidget = JobWidget.none

  readonly jobWidgetEvents$

  constructor(
    readonly _jobEvents$: Observable<RealtimeEvent>,
    private readonly _fetchQuestions: FetchQuestionsFn,
    private readonly _listSearchableJobs: ListSearchableJobsFn,
    private readonly _getCurrentWorkspace: GetCurrentWorkspaceFn,
  ) {
    this.jobWidgetEvents$ = this._jobEvents$.pipe(
      filter((event) =>
        [
          RealtimeEvent.TypeCase.JOB_CREATED,
          RealtimeEvent.TypeCase.JOB_UPDATED,
          RealtimeEvent.TypeCase.JOB_PROGRESSION,
        ].includes(event.getTypeCase()),
      ),
      concatMap((event) =>
        withAbortSignal((signal) => this._onRemoteEvent(event, signal)),
      ),
    )
  }

  async fetchJob(signal?: AbortSignal): Promise<void> {
    const startDate = `${new Date().getFullYear()}-01-01`
    const workspaceId = this._getCurrentWorkspace().id

    const [underConstructionJobs, inProgressJobs] = await Promise.all([
      this._listSearchableJobs({
        limit: 1,
        filter: `workspace_id = "${workspaceId}" AND status = "UNDER_CONSTRUCTION" AND date_created >= "${startDate}"`,
        orderBy: 'date_created',
        after: null,
      }),
      this._listSearchableJobs({
        limit: 1,
        filter: `workspace_id = "${workspaceId}" AND status != "UNDER_CONSTRUCTION" AND status != "CLOSED" AND date_created >= "${startDate}"`,
        orderBy: 'date_created desc',
        after: null,
      }),
    ])

    signal?.throwIfAborted()

    const job = inProgressJobs.data.at(0) ?? underConstructionJobs.data.at(0)

    if (job) {
      this._setJobWidget({
        name: job.displayName,
        id: job.id,
        status: job.status,
        total: job.total,
        intakeCompleted: await this._fetchIntakeQuestionsCompleted(
          job.id,
          job.status,
          signal,
        ),
        progressionStep: job.progressionStep,
        scopeChangeStatus: job.jobScopeChangeStatus,
        consultationStatus: job.consultation.status,
        consultationBookedTime: job.consultation.bookedTime,
      })
    }
  }

  @action
  private _setJobWidget(jobWidgetProps: JobWidgetProps) {
    this.jobWidget = new JobWidget().set(jobWidgetProps)
  }

  private async _onRemoteEvent(event: RealtimeEvent, signal?: AbortSignal) {
    switch (event.getTypeCase()) {
      case RealtimeEvent.TypeCase.JOB_CREATED:
      case RealtimeEvent.TypeCase.JOB_UPDATED: {
        const eventJob = event.getJobCreated() ?? event.getJobUpdated()
        const job = eventJob?.getJob()
        const jobProgression = event.getJobProgression()
        const jobId = job?.getId() ?? jobProgression?.getId()
        const jobWorkspaceId = job?.getWorkspaceId()

        // prevent from reacting to a job that's not the one in the widget
        if (
          (jobWorkspaceId !== this._getCurrentWorkspace().id &&
            this.jobWidget.id !== jobId &&
            this.jobWidget.id !== Id.none &&
            this.jobWidget.status === 'Closed') ||
          !job
        ) {
          return
        }

        const consultation = job.getConsultation()
        const consultationStatus = consultation
          ? mapConsultationStatusProtoToStatus(consultation.getStatus())
          : 'Unknown'
        const consultationBookedTime = maybeParseIntervalToTimeRange(
          consultation?.getBookedTime(),
        )

        const jobWidgetProps: JobWidgetProps = {
          id: jobId,
          status: mapJobProtoStatusToStatus(job?.getStatus()),
          intakeCompleted: await this._fetchIntakeQuestionsCompleted(
            jobId,
            mapJobProtoStatusToStatus(job.getStatus()),
            signal,
          ),
          name: mapJobProtoToTitle(job),
          total: safeDecimalToNumber(job.getTotal()),
          progressionStep: mapStepStatusProtoToStepStatus(
            job.getProgressionStep(),
          ),
          scopeChangeStatus: mapJobScopeChangeStatusProtoToStatus(
            job.getScopeChangeStatus(),
          ),
          consultationStatus,
          consultationBookedTime,
        }

        this._setJobWidget(jobWidgetProps)

        break
      }
      case RealtimeEvent.TypeCase.JOB_PROGRESSION: {
        const jobProgression = event.getJobProgression()

        // prevent from reacting to a job that's not the one in the widget
        if (this.jobWidget.id !== jobProgression?.getJobId()) {
          return
        }

        const currentStep = jobProgression?.getProgressionStepsList().at(-1)

        if (currentStep) {
          this.jobWidget.set({
            progressionStep: mapStepProtoToProps(currentStep).step,
          })
        }

        break
      }
    }
  }

  private async _fetchIntakeQuestionsCompleted(
    jobId: Id,
    jobStatus: JobStatus,
    signal?: AbortSignal,
  ) {
    if (jobStatus === 'UnderConstruction') {
      const result = await this._fetchQuestions({ jobId })
      signal?.throwIfAborted()

      return result.intakeCompleted
    }

    return true
  }
}
